import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation, useOutletContext } from "react-router-dom";
import sessionStorage from "./utils/sessionStore";
import {
  Spinner, Alert, Form, Button, Container,
} from "react-bootstrap";
import "./pagecss/Webappcss.css";
import { ScalarCell } from "./components/NestedDataGrid";
import SearchableSelect from "./components/SearchableSelect";
import CollapsibleFilters from "./components/CollapsibleFilters";
import { withRefDataPreload } from "./utils/refDataConfigs";
import { FaEdit, FaEye, FaPlus } from "react-icons/fa";
import { findMenuBySlug } from "./utils/menuSlug";
import { hasAction, canAccessMenu } from "./utils/auth";
import "./pagecss/DynamicQw.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

// ---------------------------------------------------------------------------
// Role helpers
// ---------------------------------------------------------------------------
function isAdminOrDistributor() {
  const role = (sessionStorage.getItem("userRole") || "").toLowerCase();
  return role === "admin" || role === "distr";
}
function isD99Admin() {
  return isAdminOrDistributor() && (sessionStorage.getItem("territoryId") || "").toUpperCase() === "D99";
}
// Territory (2064) and Sales Rep (2065) records are view-only unless the
// login's action entitlements (JWT "actions" claim, RoleActions table) grant
// edit — 00003 (edit territory) / 00006 (edit sales rep). Sessions from
// before entitlements shipped fall back to the old role check until the
// user re-logs in. Mirrors DynamicForm's isViewOnlyForRole so the search
// row action matches the form behaviour.
function isViewOnlyForRole(menuId) {
  if (menuId === "2064") return !hasAction("00003", isAdminOrDistributor());
  // Rep-page edit (00013) or territory-context rep edit (00006) both unlock
  // the pencil — the same form serves both paths.
  if (menuId === "2065")
    return !(hasAction("00013", isAdminOrDistributor()) || hasAction("00006", false));
  return false;
}
// Who may use the "Add New" button per menu — driven by action entitlements:
//   2064 (territories) → 00002 Add new territory   (fallback: D99 admins)
//   2065 (sales reps)  → 00012 Add new sales rep   (fallback: admin/distributor)
//   everything else    → anyone with a form
function canCreate(menuId) {
  if (menuId === "2064") return hasAction("00002", isD99Admin());
  if (menuId === "2065") return hasAction("00012", isAdminOrDistributor());
  return true;
}

// Ribbon icon per module — Territory (2064) gets a folded-map glyph, Sales Reps
// (2065) a people glyph; everything else falls back to a generic list glyph.
function HeaderIcon({ menuId }) {
  const common = {
    width: 22, height: 22, viewBox: "0 0 24 24", fill: "none",
    stroke: "currentColor", strokeWidth: 2.5, strokeLinecap: "round", strokeLinejoin: "round",
  };
  if (menuId === "2064") {
    return (
      <svg {...common}>
        <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6" />
        <line x1="8" y1="2" x2="8" y2="18" />
        <line x1="16" y1="6" x2="16" y2="22" />
      </svg>
    );
  }
  if (menuId === "2065") {
    return (
      <svg {...common}>
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Build query string from DB-driven filter fields (fully generic)
// ---------------------------------------------------------------------------
// Fixed results-per-page across all search modules. The page-size ("Top")
// filter field is hidden from the UI; this value is sent on every query.
const DEFAULT_PAGE_SIZE = 25;

function buildQueryString(filterFields, filterValues) {
  const params = new URLSearchParams();

  filterFields.forEach((field) => {
    if (!field.apiParamName) return;
    const raw = filterValues[field.fieldId];
    if (raw === null || raw === undefined || raw === "" || raw === "All") return;

    // If field has value mappings, translate display value to API value
    if (field.valueMappings) {
      try {
        const mappings = typeof field.valueMappings === "string"
          ? JSON.parse(field.valueMappings)
          : field.valueMappings;

        // OData-style template: {"valueTemplate":"startswith(CustID,'{value}')"}
        if (typeof mappings.valueTemplate === "string") {
          params.append(field.apiParamName, mappings.valueTemplate.replace("{value}", raw));
          return;
        }

        // Direct map: {"Active":"Inactive eq false","Inactive":"Inactive eq true","All":""}
        if (mappings[raw] !== undefined) {
          if (mappings[raw] !== "") {
            params.append(field.apiParamName, mappings[raw]);
          }
          return;
        }
      } catch { /* fall through to raw value */ }
    }

    params.append(field.apiParamName, raw);
  });

  return params.toString();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function DynamicSearch() {
  // Route is /:moduleSlug/search — module name first, action verb second.
  // moduleSlug is either a numeric menuId (legacy) or a slug. The hardcoded
  // role gates (menuId === "2064" / "2065") still want the numeric form, so
  // we resolve via the menu tree the navbar shares through outlet context.
  const { moduleSlug } = useParams();
  const { menuData = [] } = useOutletContext() || {};
  const isNumeric = /^\d+$/.test(moduleSlug || '');
  const menuId = isNumeric
    ? moduleSlug
    : (findMenuBySlug(menuData, moduleSlug)?.menuId?.toString() || null);
  // Preserve the form the user arrived with for outbound /:slug/create | edit
  // navigation so URLs stay consistent across hops.
  const routeSegment = moduleSlug;
  const navigate   = useNavigate();
  const location   = useLocation();
  // One-shot flash message passed via route state (e.g. by DynamicForm after
  // a successful save/update). The search page does not auto-run a search —
  // it just shows this banner so the user can confirm the action and then
  // decide what to search for.
  const [flashMessage, setFlashMessage] = useState(
    () => location.state?.flashMessage || null
  );

  // -- Search config from DB (API URL, response paths) ----------------------
  const [searchConfig,  setSearchConfig]  = useState(null);

  // -- Config from DB --------------------------------------------------------
  const [filterFields,  setFilterFields]  = useState([]);
  const [columns,       setColumns]        = useState([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError,   setConfigError]   = useState(null);

  // -- Filter state ----------------------------------------------------------
  const [filterValues, setFilterValues] = useState({});

  // -- Search results --------------------------------------------------------
  const [results,     setResults]     = useState([]);
  const [totalCount,  setTotalCount]  = useState(0);
  const [skip,        setSkip]        = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Filter panel collapses to a ribbon once the user scrolls the results grid.
  // On small screens (phones / heavy zoom) it starts collapsed so the results are
  // visible immediately — the ribbon stays above the scroll, tap to expand.
  const [filtersCollapsed, setFiltersCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 992
  );
  // Bottom-fade cue: true when the results grid has more rows scrolled off below.
  const resultsScrollRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const refreshScrollCue = () => {
    const el = resultsScrollRef.current;
    if (!el) { setMoreBelow(false); return; }
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 8);
  };
  const onResultsScroll = (e) => {
    if (e.currentTarget.scrollTop > 24 && !filtersCollapsed) setFiltersCollapsed(true);
    refreshScrollCue();
  };
  // Recompute the fade cue after results render or the panel collapses/expands
  // (both change the scroll area's height).
  useEffect(() => {
    const id = window.setTimeout(refreshScrollCue, 0);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, filtersCollapsed, isSearching]);
  const [hasDynamicForm, setHasDynamicForm] = useState(false);

  // -------------------------------------------------------------------------
  // Load search config + filter fields + result columns from DB
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!menuId) return;
    setConfigLoading(true);
    setConfigError(null);
    // Reset search state when navigating to a different menu
    setResults([]);
    setTotalCount(0);
    setSkip(0);
    setHasSearched(false);
    setSearchError(null);

    // cache: 'no-store' belt-and-braces with the controller-level
    // [ResponseCache(NoStore = true)] — together they guarantee a row change
    // in dbo.DynamicFormField shows up on the very next page load with no
    // hard-refresh needed.
    const noCache = { cache: 'no-store' };
    Promise.all([
      fetch(`${API_URL}/api/Common/DynamicFormFields/${menuId}`, noCache).then((r) => {
        if (!r.ok) throw new Error(`Fields: ${r.status}`);
        return r.json();
      }),
      fetch(`${API_URL}/api/Common/DynamicFormResultColumns/${menuId}`, noCache).then((r) => {
        if (!r.ok) throw new Error(`Columns: ${r.status}`);
        return r.json();
      }),
      fetch(`${API_URL}/api/Common/SearchConfig/${menuId}`, noCache).then((r) => {
        if (!r.ok) throw new Error(`SearchConfig: ${r.status}`);
        return r.json();
      }),
    ])
      .then(([fields, flatColumns, config]) => {
        // Hide the page-size ("Top") field — page size is fixed at DEFAULT_PAGE_SIZE.
        const searchFields = fields.filter((f) => f.formMode === "search" && f.apiParamName !== "Top");
        const entryFields  = fields.filter((f) => f.formMode !== "search");
        // Territory (2064) search filters — including Comments, the Parent
        // Territory "contains" param (ParentTerritory_c) and Parent Territory
        // Description — are now all real DynamicFormField config rows, so they
        // arrive from the menu like every other filter. No client-side
        // injection. Backend support lives in GetTerritoriesInfoHandler
        // (in-memory contains on Comment / ParentTerritory_c / ParentTerritoryDesc).
        setFilterFields(searchFields);
        setHasDynamicForm(entryFields.length > 0);
        // Use top-level columns only (no parentColumnId)
        setColumns(flatColumns.filter((c) => !c.parentColumnId));
        setSearchConfig(config);

        const defaults = {};
        searchFields.forEach((f) => {
          defaults[f.fieldId] = f.defaultValue ?? "";
        });
        setFilterValues(defaults);
      })
      .catch((e) => setConfigError(e.message))
      .finally(() => setConfigLoading(false));
  }, [menuId]);

  // -------------------------------------------------------------------------
  // Flash message lifecycle:
  //   1. Clear route state on mount so a refresh doesn't re-show the banner.
  //   2. Auto-dismiss after 4 seconds (user can also dismiss manually).
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (location.state?.flashMessage) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!flashMessage) return;
    const t = setTimeout(() => setFlashMessage(null), 4000);
    return () => clearTimeout(t);
  }, [flashMessage]);

  // Debounced auto-search — re-runs whenever a filter changes, and once on mount
  // after config loads (filterValues is seeded with the field defaults). Mirrors
  // the Customer page's keystroke-driven search, so there's no Search button.
  useEffect(() => {
    if (configLoading) return;
    if (!searchConfig?.searchApiUrl) return;
    const t = setTimeout(() => runSearch(0), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, configLoading, searchConfig]);

  // -------------------------------------------------------------------------
  // Search
  // -------------------------------------------------------------------------
  const runSearch = async (currentSkip = 0) => {
    if (!searchConfig?.searchApiUrl) return;

    setIsSearching(true);
    setSearchError(null);
    setHasSearched(true);

    try {
      // Fixed page size: buildQueryString no longer emits Top (the field is
      // hidden), so append it here along with Skip for pagination.
      const parts = [buildQueryString(filterFields, filterValues), `Top=${DEFAULT_PAGE_SIZE}`].filter(Boolean);
      if (currentSkip > 0) parts.push(`Skip=${currentSkip}`);
      const separator = searchConfig.searchApiUrl.includes("?") ? "&" : "?";
      const url       = `${API_URL}${searchConfig.searchApiUrl}${separator}${parts.join("&")}`;

      const headers = {};
      const token = sessionStorage.getItem('authToken');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(url, { headers });
      if (!resp.ok) throw new Error(`API error: ${resp.status}`);
      const data = await resp.json();

      const resultsArray = data[searchConfig.searchDataPath] ?? [];
      // Prefer totalCount (full dataset size) over count (page size)
      const totalCnt     = data.totalCount ?? data[searchConfig.searchCountPath] ?? resultsArray.length;
      setResults(resultsArray);
      setTotalCount(totalCnt);
      setSkip(currentSkip);
    } catch (e) {
      setSearchError(e.message);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e) => { e.preventDefault(); runSearch(0); };

  const handleClear = () => {
    const defaults = {};
    filterFields.forEach((f) => { defaults[f.fieldId] = f.defaultValue ?? ""; });
    setFilterValues(defaults);
    setResults([]);
    setHasSearched(false);
    setSkip(0);
    setTotalCount(0);
  };

  // -------------------------------------------------------------------------
  // Parse FieldOptions JSON. The API splits comma-separated dropdown options,
  // so for autocomplete it arrives as a string[] of JSON fragments — rejoin first.
  // -------------------------------------------------------------------------
  const parseAutocompleteConfig = (raw) => {
    try {
      let s = raw;
      if (Array.isArray(s)) s = s.join(",");
      return typeof s === "string" ? JSON.parse(s) : (s || {});
    } catch { return {}; }
  };

  // -------------------------------------------------------------------------
  // Merge static `defaultParams` with `dependentParams` from sibling filter
  // values. Lets one autocomplete filter its results by another field's
  // current value — e.g. the Territory search filter narrows its dropdown
  // to the picked Parent Territory.
  //
  // FieldOptions shape:
  //   "dependentParams": [
  //     { "param": "ParentTerritory_cExact", "fromField": "ParentTerritory_cExact" }
  //   ]
  //   param      = query-string key sent to the API
  //   fromField  = ApiParamName of the sibling filter to pull the value from
  // Entries with an empty source value are omitted (so an unselected parent
  // doesn't add an empty filter).
  // -------------------------------------------------------------------------
  const mergeDefaultParams = (acConfig) => {
    const staticParams = acConfig.defaultParams || "";
    const deps = Array.isArray(acConfig.dependentParams) ? acConfig.dependentParams : [];
    if (deps.length === 0) return staticParams || undefined;
    const pairs = [];
    deps.forEach((d) => {
      if (!d?.param || !d?.fromField) return;
      const src = filterFields.find((f) => f.apiParamName === d.fromField);
      if (!src) return;
      const v = filterValues[src.fieldId];
      if (v === undefined || v === null || v === "" || v === "All") return;
      pairs.push(`${encodeURIComponent(d.param)}=${encodeURIComponent(v)}`);
    });
    const dyn = pairs.join("&");
    if (!dyn) return staticParams || undefined;
    return staticParams ? `${staticParams}&${dyn}` : dyn;
  };

  // -------------------------------------------------------------------------
  // Render a search filter field
  // -------------------------------------------------------------------------
  const renderFilterField = (field) => {
    const value = filterValues[field.fieldId] ?? "";

    switch (field.fieldType) {
      case "dropdown":
        return (
          // width:auto so the <select> sizes to its longest option instead
          // of filling the (now compact) parent Form.Group.
          <Form.Select
            size="sm"
            value={value}
            style={{ width: "auto" }}
            onChange={(e) => setFilterValues((p) => ({ ...p, [field.fieldId]: e.target.value }))}
          >
            {(field.fieldOptions || []).map((opt, i) => (
              <option key={i} value={opt}>{opt}</option>
            ))}
          </Form.Select>
        );
      case "number":
        return (
          <Form.Control
            size="sm" type="number" value={value}
            placeholder={field.placeholder || `Enter ${field.fieldLabel}`}
            onChange={(e) => setFilterValues((p) => ({ ...p, [field.fieldId]: e.target.value }))}
          />
        );
      case "autocomplete": {
        const acConfig = parseAutocompleteConfig(field.fieldOptions);
        // Resolve `dependentParams` against current filterValues so this
        // autocomplete narrows by sibling-filter selections (e.g. Territory
        // narrows by Parent Territory). Then route it through the session
        // reference-data cache when it targets a cached dataset (Territory /
        // Sales Rep / Customer) — SearchableSelect falls back to the live
        // AutocompleteField for anything it can't preload.
        const acConfigResolved = withRefDataPreload(
          { ...acConfig, defaultParams: mergeDefaultParams(acConfig) }
        );
        const populate = acConfig.populate || {};
        // Resolve target sibling fieldIds once — populate map is keyed by
        // response field name (e.g. "territoryDesc"), valued by the target
        // filter's apiParamName (preferred — stable) OR fieldLabel (legacy).
        // apiParamName is checked first so fields can hide their label
        // (FieldLabel = '') without breaking their populate target lookup.
        const targetIds = Object.entries(populate).reduce((acc, [respKey, label]) => {
          const target = filterFields.find(
            (f) => f.apiParamName === label || f.fieldLabel === label
          );
          if (target) acc.push({ respKey, fieldId: target.fieldId });
          return acc;
        }, []);
        const pickCI = (obj, key) => {
          if (obj[key] !== undefined) return obj[key];
          const lk = key.toLowerCase();
          const found = Object.keys(obj).find((k) => k.toLowerCase() === lk);
          return found ? obj[found] : undefined;
        };
        return (
          <SearchableSelect
            size="sm"
            inputWidth="100%"
            config={acConfigResolved}
            value={value}
            placeholder={field.placeholder || `Search ${field.fieldLabel}...`}
            onChange={(val) =>
              setFilterValues((p) => {
                const next = { ...p, [field.fieldId]: val };
                // Clearing the autocomplete clears its populated siblings
                if (val === "") {
                  targetIds.forEach(({ fieldId }) => { next[fieldId] = ""; });
                }
                return next;
              })
            }
            onSelectItem={(item) =>
              setFilterValues((p) => {
                const next = { ...p };
                targetIds.forEach(({ respKey, fieldId }) => {
                  const v = pickCI(item, respKey);
                  if (v !== undefined && v !== null) next[fieldId] = String(v);
                });
                return next;
              })
            }
          />
        );
      }
      default:
        return (
          <Form.Control
            size="sm" type="text" value={value}
            placeholder={field.placeholder || `Search ${field.fieldLabel}...`}
            onChange={(e) => setFilterValues((p) => ({ ...p, [field.fieldId]: e.target.value }))}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(0); } }}
          />
        );
    }
  };

  // -------------------------------------------------------------------------
  // Header-row filters (desktop): a compact filter under each matching column
  // header. Filters are matched to columns by label / field name; any filter
  // that doesn't map to a visible column falls into a small strip above the grid.
  // -------------------------------------------------------------------------
  const hfNorm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "")
    .replace(/(cexact|ccontains|exact|contains|from|to)$/g, "");
  // Match score for filter↔column: 2 = exact, 1 = substring, 0 = none.
  const hfScore = (f, col) => {
    const fl = hfNorm(f.fieldLabel), ch = hfNorm(col.columnHeader);
    const fp = hfNorm(f.apiParamName), cf = hfNorm(col.apiFieldName);
    if ((fl && ch && fl === ch) || (fp && cf && fp === cf)) return 2;
    if ((fl && ch && (fl.includes(ch) || ch.includes(fl))) || (fp && cf && (fp.includes(cf) || cf.includes(fp)))) return 1;
    return 0;
  };
  // Assign each filter to at most ONE column — exact matches first, then
  // substrings — so e.g. the "Parent Territory" filter doesn't also land under
  // the "Parent Territory Description" column (which would mirror the value).
  const columnFilterMap = {};
  {
    const usedFilters = new Set();
    for (const minScore of [2, 1]) {
      for (const col of columns) {
        if (columnFilterMap[col.resultColumnId]) continue;
        const match = filterFields.find((ff) => !usedFilters.has(ff.fieldId) && hfScore(ff, col) >= minScore);
        if (match) { columnFilterMap[col.resultColumnId] = match; usedFilters.add(match.fieldId); }
      }
    }
  }
  const filterForColumn = (col) => columnFilterMap[col.resultColumnId];
  // Compact control for a header cell. Autocompletes degrade to a plain
  // type-to-filter text input so they fit a narrow column.
  const renderHeaderFilter = (field) => {
    if (!field) return null;
    const value = filterValues[field.fieldId] ?? "";
    const set = (v) => setFilterValues((p) => ({ ...p, [field.fieldId]: v }));
    if (field.fieldType === "dropdown") {
      return (
        <Form.Select size="sm" value={value} onChange={(e) => set(e.target.value)} style={{ fontSize: "12px" }}>
          {(field.fieldOptions || []).map((opt, i) => <option key={i} value={opt}>{opt}</option>)}
        </Form.Select>
      );
    }
    return (
      <Form.Control
        size="sm"
        type={field.fieldType === "number" ? "number" : "text"}
        value={value}
        placeholder="Filter…"
        onChange={(e) => set(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(0); } }}
        style={{ fontSize: "12px" }}
      />
    );
  };

  // -------------------------------------------------------------------------
  // Pagination math — lifted to component scope so the pager can render as a
  // FIXED FOOTER outside the scrolling results region (mirrors the filters that
  // stay pinned at the top; only the rows scroll between them).
  // -------------------------------------------------------------------------
  const pageSize    = DEFAULT_PAGE_SIZE;
  const totalPages  = Math.max(1, Math.ceil(totalCount / pageSize));
  const currentPage = Math.floor(skip / pageSize);
  const showFrom    = results.length === 0 ? 0 : skip + 1;
  const showTo      = skip + results.length;
  const goToPage    = (page) => runSearch(page * pageSize);
  const maxVisible  = 7;
  let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
  let endPage   = Math.min(totalPages, startPage + maxVisible);
  if (endPage - startPage < maxVisible) startPage = Math.max(0, endPage - maxVisible);
  const pageNumbers = [];
  for (let i = startPage; i < endPage; i++) pageNumbers.push(i);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  // Module-level entitlement (Menu.RequiredActionId via the shared menu tree):
  // a role without the module's search action can't open this page by URL.
  // (Placed after all hooks — React needs a stable hook order.)
  const menuEntry = menuData.find((m) => String(m.menuId) === String(menuId));
  if (menuEntry && !canAccessMenu(menuEntry)) {
    return (
      <div className="qw-root content-halo content-halo-narrow" style={{ maxWidth: 520, margin: "120px auto", padding: "36px 32px", borderRadius: 16, textAlign: "center" }}>
        <h4 style={{ color: "#0F172A", fontWeight: 700 }}>No access to this page</h4>
        <p style={{ color: "#64748B", fontSize: 14 }}>Your role is not entitled to this module. Changes to role access apply at your next login.</p>
      </div>
    );
  }
  return (
    <div style={{ display: "flow-root" }}>
    <div className="qw-root content-halo dyn-qw dyn-search-shell">
      <Container fluid className="px-0" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>

        {configLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" /> Loading search configuration...
          </div>
        ) : configError ? (
          <Alert variant="danger">{configError}</Alert>
        ) : (
          <>
            {/* -- Ribbon header — icon left, centered title, Add button right -- */}
            <div className="qw-header qw-header-centered" style={{ marginBottom: 16 }}>
              <div className="qw-header-icon">
                <HeaderIcon menuId={menuId} />
              </div>
              <div className="qw-header-title">{searchConfig?.menuTitle ?? "Search"}</div>
              {hasDynamicForm && canCreate(menuId) && (
                <button
                  className="qw-btn cust-add-btn"
                  style={{ background: "#fff", color: "#4F46E5", fontWeight: 700 }}
                  onClick={() => navigate(`/${routeSegment}/create`)}
                  title="Add new"
                  aria-label="Add new"
                >
                  <FaPlus className="cust-add-plus" />
                  <span className="d-none d-lg-inline ms-2">Add New</span>
                </button>
              )}
            </div>

            {/* -- Search filter panel — mobile only (cards have no header row).
                   Desktop filters live in the grid header, which now always
                   renders (even with zero rows). It sits ABOVE the internal results
                   scroll, so it stays put while the cards scroll; the first scroll
                   collapses it to a tappable "Filters" ribbon (and it starts
                   collapsed on small screens so results are visible immediately). -- */}
            <div className="d-lg-none">
            <CollapsibleFilters collapsed={filtersCollapsed} onCollapsedChange={setFiltersCollapsed}>
            <div
              className="dyn-qw-panel"
              style={{
                padding: "16px 20px",
                marginBottom: "20px",
              }}
            >
              <Form onSubmit={handleSearch}>
                {/* 2-column grid (matches the Customer page). Layout is in CSS
                    (.dyn-qw-filters); each field fills its cell. */}
                <div className="dyn-qw-filters">
                  {filterFields.map((field) => (
                      <Form.Group
                        key={field.fieldId}
                        className="dyn-qw-filter-group"
                      >
                        {/* Every field type — autocompletes included — gets the
                            same external uppercase label, so the panel reads as
                            ONE uniform grid (matches the Quotes/Orders panels). */}
                        <Form.Label className="dyn-qw-filter-label">
                          {/* Render NBSP when a field's FieldLabel is blank
                              so the label slot still reserves its line height —
                              keeps this input baseline-aligned with sibling
                              filters that DO have labels. Used by fields
                              whose meaning is unambiguous from a neighbouring
                              field (e.g. Territory ID, identified by the
                              adjacent Territory Description it populates). */}
                          {field.fieldLabel && field.fieldLabel.trim()
                            ? field.fieldLabel
                            : ' '}
                        </Form.Label>
                        {renderFilterField(field)}
                      </Form.Group>
                  ))}
                  {/* Compact Clear pinned to the right corner of its own
                      full-width row — same actions treatment as the Quotes
                      panel (the button no longer stretches across the grid). */}
                  {/* NOT .dyn-qw-filter-actions — that class's <992px rule
                      stretches its buttons to fill the row (meant for
                      Search+Clear pairs); a lone stretched Clear looked odd. */}
                  <div className="dyn-qw-filter-group" style={{ gridColumn: "1 / -1" }}>
                    <div className="d-flex gap-2" style={{ justifyContent: "flex-end" }}>
                      <Button type="button" variant="outline-secondary" size="sm" onClick={handleClear}>
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            </div>
            </CollapsibleFilters>
            </div>

            {/* -- Flash message (e.g. "Territory saved successfully") -- */}
            {flashMessage && (
              <Alert
                variant={flashMessage.variant || "success"}
                dismissible
                onClose={() => setFlashMessage(null)}
                style={{ marginTop: "12px" }}
              >
                {flashMessage.text}
              </Alert>
            )}

            {/* -- Search error -- */}
            {searchError && <Alert variant="danger">{searchError}</Alert>}

            {/* Results scroll INTERNALLY so the page itself doesn't scroll; the
                first scroll collapses the filter panel to its ribbon. The fade
                at the bottom signals there are more rows below. */}
            <div className="dyn-results-wrap">
            <div
              ref={resultsScrollRef}
              className="dyn-results-scroll"
              onScroll={onResultsScroll}
            >
            {/* -- Results -- The grid (and its header filters) render whenever a
                 search has run, regardless of isSearching/empty, so the filter
                 inputs never unmount (keeps focus) and the desktop filters stay
                 put even with zero rows. -- */}
            {hasSearched && (() => {
              return (
                <>
                      {/* Filters with no matching column — desktop strip above the grid. */}
                      {(() => {
                        const mapped = new Set();
                        columns.forEach((c) => { const ff = filterForColumn(c); if (ff) mapped.add(ff.fieldId); });
                        const unmapped = filterFields.filter((ff) => !mapped.has(ff.fieldId));
                        if (!unmapped.length) return null;
                        return (
                          <div className="d-none d-lg-flex" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                            {unmapped.map((ff) => {
                              // The strip has room (unlike the narrow in-grid header
                              // cells), so render autocompletes as the REAL suggestions
                              // control instead of degrading to a text box. The card
                              // variant carries its own eyebrow label, so skip the
                              // external one for it; text/dropdown keep the compact box.
                              const isAuto = ff.fieldType === "autocomplete";
                              return (
                                // Autocomplete slot sizes to its content so the selected
                                // code + full description shows (the card's desc is
                                // nowrap+ellipsis, so a hard width clips it). A wide
                                // minWidth keeps the empty state looking wide; maxWidth
                                // caps very long descriptions. Text/dropdown stay compact.
                                <div key={ff.fieldId}
                                  className={isAuto ? "dyn-strip-auto" : undefined}
                                  style={isAuto
                                  ? { flex: "0 1 auto", minWidth: 360, maxWidth: 560 }
                                  : { flex: "0 0 180px" }}>
                                  {!isAuto && (
                                    <div style={{ fontSize: "10px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: ".04em", marginBottom: 3 }}>
                                      {ff.fieldLabel && ff.fieldLabel.trim() ? ff.fieldLabel : "Filter"}
                                    </div>
                                  )}
                                  {isAuto ? renderFilterField(ff) : renderHeaderFilter(ff)}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}
                      {/* -- Results table (desktop ≥992px) -- built from DB column config.
                          No overflow here: an overflow container would re-anchor the
                          sticky <thead> to itself instead of the scroll region. -- */}
                      <div className="d-none d-lg-block" style={{ border: "1px solid #E2E8F0", borderRadius: 12, background: "#fff" }}>
                        <table
                          style={{
                            width: "100%",
                            tableLayout: "fixed",
                            borderCollapse: "collapse",
                            fontSize: "13px",
                          }}
                        >
                          <thead>
                            <tr style={{ background: "#F8FAFC", textAlign: "left", color: "#64748B", fontSize: "11px", textTransform: "uppercase", letterSpacing: ".04em" }}>
                              {hasDynamicForm && (
                                <th
                                  style={{
                                    padding: "9px 12px",
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                    width: "50px",
                                    textAlign: "center",
                                    // Label and filter rows form one cohesive header — the
                                    // header/body divider lives on the filter row below.
                                    borderBottom: "none",
                                  }}
                                >
                                  {isViewOnlyForRole(menuId) ? "View" : "Edit"}
                                </th>
                              )}
                              {columns.map((col) => {
                                const hasMaxW = col.columnWidth && col.columnWidth !== "auto";
                                return (
                                  <th
                                    key={col.resultColumnId}
                                    style={{
                                      padding: "9px 12px",
                                      fontWeight: 700,
                                      whiteSpace: "nowrap",
                                      borderBottom: "none",
                                      ...(hasMaxW ? { width: col.columnWidth, maxWidth: col.columnWidth } : {}),
                                    }}
                                  >
                                    {col.columnHeader}
                                  </th>
                                );
                              })}
                            </tr>
                            {/* Filter row — a compact filter under each matching column. */}
                            <tr className="dyn-hf-row">
                              {hasDynamicForm && (
                                <th style={{ padding: "4px 6px", textAlign: "center", background: "#F1F5F9" }}>
                                  <button
                                    type="button"
                                    onClick={handleClear}
                                    title="Clear filters"
                                    style={{ border: "none", background: "transparent", color: "#64748B", fontSize: "13px", cursor: "pointer", lineHeight: 1 }}
                                  >
                                    &times;
                                  </button>
                                </th>
                              )}
                              {columns.map((col) => {
                                const hasMaxW = col.columnWidth && col.columnWidth !== "auto";
                                return (
                                  <th
                                    key={col.resultColumnId}
                                    style={{ padding: "4px 6px", background: "#F1F5F9", ...(hasMaxW ? { width: col.columnWidth, maxWidth: col.columnWidth } : {}) }}
                                  >
                                    {renderHeaderFilter(filterForColumn(col))}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {results.length === 0 ? (
                              <tr>
                                <td colSpan={columns.length + (hasDynamicForm ? 1 : 0)} style={{ padding: "26px 12px", textAlign: "center", color: "#94A3B8", fontSize: "13px" }}>
                                  {isSearching ? "Searching…" : "No results found."}
                                </td>
                              </tr>
                            ) : results.map((row, ri) => (
                              <tr key={ri}>
                                {hasDynamicForm && (
                                  <td
                                    style={{
                                      padding: "9px 12px",
                                      verticalAlign: "middle",
                                      textAlign: "center",
                                    }}
                                  >
                                    {isViewOnlyForRole(menuId) ? (
                                      <FaEye
                                        style={{
                                          color: "#0052A5",
                                          cursor: "pointer",
                                          fontSize: "16px",
                                        }}
                                        title="View"
                                        onClick={() => navigate(`/${routeSegment}/edit`, { state: { editData: row } })}
                                      />
                                    ) : (
                                      <FaEdit
                                        style={{
                                          color: "#0052A5",
                                          cursor: "pointer",
                                          fontSize: "16px",
                                        }}
                                        title="Edit"
                                        onClick={() => navigate(`/${routeSegment}/edit`, { state: { editData: row } })}
                                      />
                                    )}
                                  </td>
                                )}
                                {columns.map((col) => {
                                  const rawVal = row[col.apiFieldName];
                                  const hasMaxW = col.columnWidth && col.columnWidth !== "auto";
                                  return (
                                    <td
                                      key={col.resultColumnId}
                                      style={{
                                        padding: "9px 12px",
                                        verticalAlign: "middle",
                                        // Columns with a configured width wrap within it; columns
                                        // without one get a sensible cap so long text (e.g. comments)
                                        // wraps instead of overflowing the cell.
                                        ...(hasMaxW
                                          ? { maxWidth: col.columnWidth, whiteSpace: "normal", wordWrap: "break-word" }
                                          : { maxWidth: 320, whiteSpace: "normal", wordWrap: "break-word" }),
                                      }}
                                    >
                                      <ScalarCell col={col} value={rawVal} />
                                    </td>
                                  );
                                })}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* -- Results cards (mobile <992px) -- one card per row,
                          values flow inline; status + action pinned top-right.
                          Replaces the side-scrolling grid. -- */}
                      <div className="d-lg-none dyn-search-cards">
                        {results.length === 0 ? (
                          <div style={{ textAlign: "center", color: "#94A3B8", fontSize: "13px", padding: "24px 0" }}>
                            {isSearching ? "Searching…" : "No results found."}
                          </div>
                        ) : (() => {
                          const nonEmpty = (v) => v !== null && v !== undefined && v !== "";
                          const colBy = (name) =>
                            columns.find((c) => c.apiFieldName && c.apiFieldName.toLowerCase() === name);
                          // The Active/Inactive status renders as a compact colour circle.
                          const statusCol = colBy("inactive");

                          // Shared Edit/View action icon (role-gated like the table).
                          const actionIcon = (row) => {
                            if (!hasDynamicForm) return null;
                            return isViewOnlyForRole(menuId) ? (
                              <FaEye
                                style={{ color: "#0052A5", cursor: "pointer", fontSize: "18px" }}
                                title="View"
                                onClick={() => navigate(`/${routeSegment}/edit`, { state: { editData: row } })}
                              />
                            ) : (
                              <FaEdit
                                style={{ color: "#0052A5", cursor: "pointer", fontSize: "18px" }}
                                title="Edit"
                                onClick={() => navigate(`/${routeSegment}/edit`, { state: { editData: row } })}
                              />
                            );
                          };

                          // -- Territory (2064): purpose-built 3-line card --
                          //   line 1: ID (small) · Name (grows) · status
                          //   line 2: parent ID · parent desc · edit
                          //   line 3: comment (full width)
                          const idCol         = colBy("territoryid");
                          const descCol       = colBy("territorydesc");
                          const parentIdCol   = colBy("parentterritory_c");
                          const parentDescCol = colBy("parentterritorydesc");
                          const commentCol    = colBy("comment");
                          const isTerritory   = menuId === "2064" && idCol && descCol;

                          if (isTerritory) {
                            const known = new Set(
                              [idCol, descCol, statusCol, parentIdCol, parentDescCol, commentCol].filter(Boolean)
                            );
                            return results.map((row, ri) => {
                              // Surface any other configured column we didn't place explicitly.
                              const extraCols = columns.filter(
                                (c) => !known.has(c) && nonEmpty(row[c.apiFieldName])
                              );
                              return (
                                <div key={ri} className="dyn-search-card terr-card">
                                  <div className="terr-line terr-line1">
                                    {idCol && nonEmpty(row[idCol.apiFieldName]) && (
                                      <span className="terr-id"><ScalarCell col={idCol} value={row[idCol.apiFieldName]} /></span>
                                    )}
                                    {descCol && (
                                      <span className="terr-name"><ScalarCell col={descCol} value={row[descCol.apiFieldName]} /></span>
                                    )}
                                    {statusCol && (
                                      <span className="terr-status"><ScalarCell col={statusCol} value={row[statusCol.apiFieldName]} /></span>
                                    )}
                                  </div>
                                  <div className="terr-line terr-line2">
                                    {parentIdCol && nonEmpty(row[parentIdCol.apiFieldName]) && (
                                      <span className="terr-pid"><ScalarCell col={parentIdCol} value={row[parentIdCol.apiFieldName]} /></span>
                                    )}
                                    {parentDescCol && nonEmpty(row[parentDescCol.apiFieldName]) && (
                                      <span className="terr-pname"><ScalarCell col={parentDescCol} value={row[parentDescCol.apiFieldName]} /></span>
                                    )}
                                    {hasDynamicForm && <span className="terr-edit">{actionIcon(row)}</span>}
                                  </div>
                                  {commentCol && nonEmpty(row[commentCol.apiFieldName]) && (
                                    <div className="terr-line terr-comment">
                                      <ScalarCell col={commentCol} value={row[commentCol.apiFieldName]} />
                                    </div>
                                  )}
                                  {extraCols.length > 0 && (
                                    <div className="terr-line terr-extra">
                                      {extraCols.map((c) => (
                                        <span key={c.resultColumnId} className="dyn-search-card-item">
                                          <ScalarCell col={c} value={row[c.apiFieldName]} />
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          }

                          // -- Sales Rep (2065): purpose-built 4-line card --
                          //   line 1: code (small) · name (grows) · status
                          //   line 2: title · email · edit
                          //   line 3: office phone · cell phone
                          //   line 4: city · state
                          const codeCol   = colBy("salesrepcode");
                          const srNameCol = colBy("name");
                          const titleCol  = colBy("salesreptitle");
                          const emailCol  = colBy("emailaddress");
                          const officeCol = colBy("officephonenum");
                          const cellCol   = colBy("cellphonenum");
                          const cityCol   = colBy("city");
                          const stateCol  = colBy("state");
                          const isSalesRep = menuId === "2065" && (codeCol || srNameCol);

                          if (isSalesRep) {
                            const known = new Set(
                              [codeCol, srNameCol, statusCol, titleCol, emailCol, officeCol, cellCol, cityCol, stateCol].filter(Boolean)
                            );
                            return results.map((row, ri) => {
                              const has = (c) => c && nonEmpty(row[c.apiFieldName]);
                              const extraCols = columns.filter((c) => !known.has(c) && nonEmpty(row[c.apiFieldName]));
                              return (
                                <div key={ri} className="dyn-search-card terr-card">
                                  <div className="terr-line terr-line1">
                                    {has(codeCol) && (
                                      <span className="terr-id"><ScalarCell col={codeCol} value={row[codeCol.apiFieldName]} /></span>
                                    )}
                                    {srNameCol && (
                                      <span className="terr-name"><ScalarCell col={srNameCol} value={row[srNameCol.apiFieldName]} /></span>
                                    )}
                                    {statusCol && (
                                      <span className="terr-status"><ScalarCell col={statusCol} value={row[statusCol.apiFieldName]} /></span>
                                    )}
                                  </div>
                                  <div className="terr-line terr-line2">
                                    {has(titleCol) && (
                                      <span className="sr-val"><ScalarCell col={titleCol} value={row[titleCol.apiFieldName]} /></span>
                                    )}
                                    {has(emailCol) && (
                                      <span className="sr-val sr-email"><ScalarCell col={emailCol} value={row[emailCol.apiFieldName]} /></span>
                                    )}
                                    {hasDynamicForm && <span className="terr-edit">{actionIcon(row)}</span>}
                                  </div>
                                  {(has(officeCol) || has(cellCol)) && (
                                    <div className="terr-line sr-line">
                                      {has(officeCol) && (
                                        <span className="sr-val"><ScalarCell col={officeCol} value={row[officeCol.apiFieldName]} /></span>
                                      )}
                                      {has(cellCol) && (
                                        <span className="sr-val"><ScalarCell col={cellCol} value={row[cellCol.apiFieldName]} /></span>
                                      )}
                                    </div>
                                  )}
                                  {(has(cityCol) || has(stateCol)) && (
                                    <div className="terr-line sr-line">
                                      {has(cityCol) && (
                                        <span className="sr-val"><ScalarCell col={cityCol} value={row[cityCol.apiFieldName]} /></span>
                                      )}
                                      {has(stateCol) && (
                                        <span className="sr-val"><ScalarCell col={stateCol} value={row[stateCol.apiFieldName]} /></span>
                                      )}
                                    </div>
                                  )}
                                  {extraCols.length > 0 && (
                                    <div className="terr-line sr-line">
                                      {extraCols.map((c) => (
                                        <span key={c.resultColumnId} className="sr-val">
                                          <ScalarCell col={c} value={row[c.apiFieldName]} />
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            });
                          }

                          // -- Generic card: status + action pinned top-right, values inline --
                          return results.map((row, ri) => {
                            const bodyCols = columns.filter((col) => {
                              if (col === statusCol) return false;
                              // Skip empty cells so cards stay compact (keep
                              // `false` — it's a real value).
                              return nonEmpty(row[col.apiFieldName]);
                            });
                            return (
                              <div key={ri} className="dyn-search-card">
                                {(statusCol || hasDynamicForm) && (
                                  <div className="dyn-search-card-action">
                                    {statusCol && (
                                      <ScalarCell col={statusCol} value={row[statusCol.apiFieldName]} />
                                    )}
                                    {actionIcon(row)}
                                  </div>
                                )}
                                {bodyCols.map((col) => (
                                  <span key={col.resultColumnId} className="dyn-search-card-item">
                                    <ScalarCell col={col} value={row[col.apiFieldName]} />
                                  </span>
                                ))}
                              </div>
                            );
                          });
                        })()}
                      </div>

                </>
              );
            })()}
            </div>
            {moreBelow && <div className="dyn-results-fade" aria-hidden="true" />}
            </div>

            {/* Fixed pagination footer — lives OUTSIDE the scrolling results
                region so it stays pinned at the bottom of the grid container
                (mirrors the filters pinned at the top); only the rows scroll. */}
            {hasSearched && (
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                style={{ flexShrink: 0, padding: "10px 0 0", marginTop: 10, borderTop: "1px solid #dee2e6" }}>
                <span style={{ fontSize: "13px", color: "#555" }}>
                  Showing {showFrom}&ndash;{showTo} of {totalCount} results
                </span>

                {totalPages > 1 && (
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span style={{ fontSize: "13px", color: "#555" }}>
                      Page {currentPage + 1} of {totalPages}
                    </span>

                    <div className="d-flex align-items-center gap-1 flex-wrap">
                      <Button size="sm" variant="outline-primary"
                        disabled={currentPage === 0}
                        onClick={() => goToPage(0)}>
                        &laquo;
                      </Button>
                      <Button size="sm" variant="outline-primary"
                        disabled={currentPage === 0}
                        onClick={() => goToPage(currentPage - 1)}>
                        &lsaquo; Prev
                      </Button>

                      {/* Numbered pages — desktop only. On mobile the
                          "Page X of Y" text + Prev/Next is enough and
                          the numbers would overflow the narrow width. */}
                      <div className="d-none d-lg-flex align-items-center gap-1">
                        {startPage > 0 && (
                          <span style={{ fontSize: "12px", color: "#888", padding: "0 4px" }}>&hellip;</span>
                        )}

                        {pageNumbers.map((p) => (
                          <Button key={p} size="sm"
                            variant={p === currentPage ? "primary" : "outline-primary"}
                            onClick={() => goToPage(p)}
                            style={{ minWidth: "32px" }}>
                            {p + 1}
                          </Button>
                        ))}

                        {endPage < totalPages && (
                          <span style={{ fontSize: "12px", color: "#888", padding: "0 4px" }}>&hellip;</span>
                        )}
                      </div>

                      <Button size="sm" variant="outline-primary"
                        disabled={currentPage >= totalPages - 1}
                        onClick={() => goToPage(currentPage + 1)}>
                        Next &rsaquo;
                      </Button>
                      <Button size="sm" variant="outline-primary"
                        disabled={currentPage >= totalPages - 1}
                        onClick={() => goToPage(totalPages - 1)}>
                        &raquo;
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Container>
    </div>
    </div>
  );
}

export default DynamicSearch;
